import { LockKeyhole, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { adminLogin, getAdminToken, setAdminToken } from "@/lib/api";

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (getAdminToken()) {
      navigate("/admin/leads", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousContent = robotsMeta?.getAttribute("content") || "";

    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }

    robotsMeta.setAttribute("content", "noindex, nofollow");

    return () => {
      if (previousContent) {
        robotsMeta.setAttribute("content", previousContent);
      } else {
        robotsMeta.remove();
      }
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await adminLogin(credentials);
      setAdminToken(response.access_token);
      toast.success("Admin access granted", {
        description: "You can now view and manage CareFleet website leads.",
      });
      navigate("/admin/leads", { replace: true });
    } catch (error) {
      toast.error("Unable to sign in", {
        description: error?.response?.data?.detail || "Please check your credentials and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-shell min-h-screen px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-xl">
        <div className="soft-card p-8 sm:p-10" data-testid="admin-login-card">
          <div className="space-y-4">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <p className="eyebrow">Internal admin area</p>
            <h1 className="font-heading text-4xl font-semibold text-slate-950" data-testid="admin-login-title">
              CareFleet lead dashboard login
            </h1>
            <p className="text-base leading-8 text-slate-600" data-testid="admin-login-description">
              Lightweight website-only access for viewing enquiries, tracking follow-up status, and exporting leads.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="admin-login-form">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700" htmlFor="admin-username">
                Username
              </label>
              <Input
                id="admin-username"
                value={credentials.username}
                onChange={(event) => setCredentials((prev) => ({ ...prev, username: event.target.value }))}
                className="h-12 rounded-2xl bg-white"
                data-testid="admin-username-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700" htmlFor="admin-password">
                Password
              </label>
              <Input
                id="admin-password"
                type="password"
                value={credentials.password}
                onChange={(event) => setCredentials((prev) => ({ ...prev, password: event.target.value }))}
                className="h-12 rounded-2xl bg-white"
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="rounded-full bg-brand-navy px-6 py-6 text-white hover:bg-brand-navy/90"
              data-testid="admin-login-submit-button"
            >
              <LockKeyhole className="h-4 w-4" />
              {isSubmitting ? "Signing in" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};